/**
 * Rótulos, selos e hooks da gestão de pneus — um arquivo só, para "Instalado",
 * "Atenção" e "Finatto" aparecerem iguais na lista, na ficha, no mapa e no app.
 */
import { useQuery } from "@tanstack/react-query";
import type {
  TireCapabilitiesDto, TireCapabilityDto, TireConditionDto, TireMovementTypeDto, TireProblemDto, TireRetreadStatusDto,
  TireStatusDto,
} from "@bi/types";
import { api } from "../../lib/api";

/** O que o usuário pode fazer em pneus. A API recusa de qualquer jeito (403); aqui só se esconde o botão. */
export function useTireCapabilities() {
  const q = useQuery({
    queryKey: ["tires", "capabilities"],
    queryFn: () => api.get<TireCapabilitiesDto>("/fleet/tires/capabilities"),
    staleTime: 5 * 60_000,
  });
  const lista = q.data?.capabilities ?? [];
  return { loading: q.isLoading, can: (c: TireCapabilityDto) => lista.includes(c) };
}

export const TIRE_STATUS: Record<TireStatusDto, { label: string; badge: string }> = {
  installed: { label: "Instalado", badge: "badge-green" },
  stock: { label: "Estoque", badge: "badge-blue" },
  retread: { label: "Recapagem", badge: "badge-amber" },
  written_off: { label: "Descartado", badge: "badge-gray" },
  unknown: { label: "Não localizado", badge: "badge-gray" },
};

export const TIRE_CONDITION: Record<TireConditionDto, { label: string; badge: string }> = {
  normal: { label: "Normal", badge: "badge-green" },
  attention: { label: "Atenção", badge: "badge-amber" },
  critical: { label: "Crítico", badge: "badge-red" },
};

export const PROBLEM_LABEL: Record<TireProblemDto, string> = {
  low_pressure: "Pressão baixa",
  irregular_wear: "Desgaste irregular",
  cut: "Corte",
  bulge: "Bolha",
  low_tread: "Sulco baixo",
  damage: "Avaria",
  other: "Outro",
};

export const MOVEMENT_TYPE_LABEL: Record<TireMovementTypeDto, string> = {
  installation: "Instalação",
  removal: "Retirada",
  rotation: "Rodízio",
  transfer: "Transferência",
  to_stock: "Para o estoque",
  from_stock: "Saída do estoque",
  to_retread: "Envio p/ recapagem",
  from_retread: "Retorno da recapagem",
  write_off: "Descarte",
  adjustment: "Ajuste",
};

export const RETREAD_STATUS: Record<TireRetreadStatusDto, { label: string; badge: string }> = {
  waiting_send: { label: "Aguardando envio", badge: "badge-gray" },
  sent: { label: "Enviado", badge: "badge-amber" },
  in_process: { label: "Em processo", badge: "badge-amber" },
  returned: { label: "Retornou", badge: "badge-green" },
  rejected: { label: "Recusado", badge: "badge-red" },
  condemned: { label: "Condenado", badge: "badge-red" },
  warranty: { label: "Em garantia", badge: "badge-blue" },
};

export function TireStatusBadge({ status }: { status: TireStatusDto }) {
  const s = TIRE_STATUS[status];
  return <span className={s.badge}>{s.label}</span>;
}

export function ConditionBadge({ condition }: { condition: TireConditionDto | null | undefined }) {
  if (!condition) return <span className="text-xs text-gray-400">—</span>;
  const c = TIRE_CONDITION[condition];
  return <span className={c.badge}>{c.label}</span>;
}

/** Origem do dado — aparece no detalhe, no histórico e nos rótulos técnicos. */
export function OriginBadge({ origin }: { origin: string | null | undefined }) {
  if (!origin) return null;
  const o = origin.toLowerCase();
  const label = o === "siag" ? "SIAG" : o === "finatto" ? "Finatto" : o === "sinersoft" ? "Sinersoft" : origin;
  const tone = o === "siag" ? "bg-tm-50 text-tm-700 border-tm-100" : "bg-surface-muted text-gray-500 border-surface-border";
  return <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide ${tone}`}>{label}</span>;
}

/** "2ª vida" a partir de "R1"; "Nova" para N. */
export function lifeText(life: string): string {
  if (life === "Novo") return "Nova";
  const n = Number(life.replace(/\D/g, ""));
  return Number.isFinite(n) && n > 0 ? `${n + 1}ª vida` : life;
}

export const fmtMm = (v: number | null | undefined) =>
  v == null ? "—" : `${v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mm`;
export const fmtKm = (v: number | null | undefined) => (v == null ? "—" : `${Math.round(v).toLocaleString("pt-BR")} km`);
export const fmtPsi = (v: number | null | undefined) => (v == null ? "—" : `${Math.round(v).toLocaleString("pt-BR")} PSI`);
export const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
export const fmtDateTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

/** Cores do cartão de posição no mapa: discretas, a borda carrega o estado. */
export function positionTone(condition: TireConditionDto | null | undefined, empty: boolean) {
  if (empty) return "border-dashed border-gray-300 bg-white text-gray-400";
  if (condition === "critical") return "border-danger-400 bg-danger-50";
  if (condition === "attention") return "border-warning-400 bg-warning-50";
  return "border-tm-200 bg-tm-50/60";
}

/** Abas por `?aba=` — mesmo padrão das listas de manutenção (botões com contagem). */
export function TabBar<K extends string>({ tabs, active, onChange }: {
  tabs: Array<{ key: K; label: string; count?: number | null; hidden?: boolean }>;
  active: K;
  onChange: (k: K) => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" role="tablist">
      {tabs.filter((t) => !t.hidden).map((t) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={active === t.key}
          onClick={() => onChange(t.key)}
          className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm border ${
            active === t.key ? "bg-tm-500 text-white border-tm-500" : "bg-white text-gray-600 border-surface-border hover:bg-surface-muted"
          }`}
        >
          {t.label}
          {t.count != null && <span className={`ml-2 text-xs ${active === t.key ? "text-white/80" : "text-gray-400"}`}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}
